import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "../../../../config";
import BcValueCircle from "./BcValueCircle";
import FilterableTable from "./FilterableTable";

// "A/R Over 90" widget — green circle showing the total outstanding amount
// customers owe on open invoices aged over 90 days, read live from Business
// Central. Firm-wide metric (not salesperson-scoped).
//
// Clicking the circle opens a modal with the per-invoice breakdown
// (GET /Dashboard/api/Dashboard/AROver90Detail), filterable per column.
const COLUMNS = [
  { key: "invoiceNumber", label: "Invoice Number" },
  { key: "jobNumber", label: "Job Number" },
  { key: "propertyName", label: "Property Name" },
  { key: "unitNo", label: "Unit#" },
  { key: "salesPerson", label: "Sales Person", type: "select" },
  { key: "jobDescription", label: "Job Description" },
  { key: "invoiceDueDate", label: "Invoice Due Date", type: "date", noFilter: true  },
];

const AROver90 = ({ auth: { auth }, dataScope = "all", userId }) => {
  const [value, setValue] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");

  const token = auth && auth[0] ? auth[0].token : null;
  const salesPersonId = auth && auth[0] ? auth[0]?.salesPersonId : null;
  let tmpUserId = userId;
  if (auth && (auth[0]?.roleName === "Sales" && salesPersonId !== "")) {
    tmpUserId = salesPersonId;
  }
  const scopedSalesPersonId = dataScope === "self" ? tmpUserId : null;

  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    const params = {};
    if (scopedSalesPersonId) params.salesPersonId = scopedSalesPersonId;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/AROver90`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setValue(rows[0] ? rows[0].value : 0);
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load A/R Over 90:", err.message);
      });
    return () => {
      active = false;
    };
  }, [token]);

  // Fetch the per-invoice breakdown lazily, the first time the modal opens.
  const openDetails = () => {
    setShowDetails(true);
    if (detailStatus !== "idle" || !token) return;
    setDetailStatus("loading");
    const params = {};
    if (scopedSalesPersonId) params.salesPersonId = scopedSalesPersonId;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/AROver90Detail`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => {
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setDetail(rows);
        setDetailStatus("ok");
      })
      .catch((err) => {
        console.warn("Could not load A/R Over 90 detail:", err.message);
        setDetailStatus("error");
      });
  };

  return (
    <>
      <BcValueCircle title="A/R Over 90" value={value} onClick={openDetails} />

      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="xl"
        centered
        scrollable
        dialogClassName="ft-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>A/R Over 90</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailStatus === "loading" ? (
            <div className="ft-status">Loading…</div>
          ) : detailStatus === "error" ? (
            <div className="ft-status">Could not load the A/R Over 90 details.</div>
          ) : (
            <FilterableTable
              columns={COLUMNS}
              rows={detail || []}
              rowKey="invoiceNumber"
              emptyText="No invoices are over 90 days past due."
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(AROver90);
